/**
 * Webtech HR Engine™ - Checkout Page
 * Page de paiement avec formulaire de facturation
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Check,
  CreditCard,
  Building2,
  Mail,
  Phone,
  User,
  FileText,
  MapPin,
  ArrowLeft,
  Loader2,
  Shield,
  Lock,
  Crown,
  Sparkles
} from 'lucide-react';

export default function CheckoutPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState(null);
  
  // Get state from navigation
  const { priceInfo, userCount, frequency, promoCode } = location.state || {};
  
  // Form state
  const [formData, setFormData] = useState({
    companyName: '',
    companyAddress: '',
    companyEmail: '',
    companyNif: '',
    companyPhone: '',
    contactName: ''
  });
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!priceInfo) {
      navigate('/billing/pricing');
      return;
    }
    fetchCompanyInfo();
  }, [priceInfo]);

  const fetchCompanyInfo = async () => {
    try {
      if (user?.companyId) {
        const response = await api.get(`/companies/${user.companyId}`);
        const companyData = response.data;
        setCompany(companyData);
        
        // Pre-fill form
        setFormData(prev => ({
          ...prev,
          companyName: companyData.name || '',
          companyEmail: user.email || '',
          contactName: `${user.firstName} ${user.lastName}`
        }));
      }
    } catch (error) {
      console.error('Error fetching company:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error on change
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Nom de l\'entreprise requis';
    }
    if (!formData.companyEmail.trim()) {
      newErrors.companyEmail = 'Email requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.companyEmail)) {
      newErrors.companyEmail = 'Email invalide';
    }
    if (!formData.companyPhone.trim()) {
      newErrors.companyPhone = 'Téléphone requis';
    }
    if (!formData.contactName.trim()) {
      newErrors.contactName = 'Nom du contact requis';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Veuillez corriger les erreurs du formulaire');
      return;
    }
    
    setLoading(true);
    try {
      // First, update subscription details
      await api.post('/billing/subscription/upgrade', {
        userCount,
        frequency,
        promoCode
      });
      
      // Then process checkout with userCount and frequency
      const response = await api.post('/billing/checkout', {
        subscriptionId: 'pending', // Will be resolved by backend
        userCount,
        frequency,
        ...formData,
        promoCode
      });
      
      const { paymentUrl, paymentId } = response.data;
      
      if (!paymentUrl) {
        toast.error('Erreur: URL de paiement non reçue');
        return;
      }
      
      // Redirection vers PayTech pour le paiement
      toast.info('Redirection vers PayTech...');
      window.location.href = paymentUrl;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount);
  };

  if (!priceInfo) {
    return null;
  }

  return (
    <DashboardLayout title="Paiement">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/billing/pricing')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Finaliser votre commande</h1>
            <p className="text-slate-500">Passage au plan PRO</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: Billing form */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-webtech-blue" />
                  Informations de facturation
                </CardTitle>
                <CardDescription>Ces informations apparaîtront sur vos factures</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="companyName">Nom de l'entreprise *</Label>
                    <Input
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="Webtech Solutions"
                      className={errors.companyName ? 'border-red-500' : ''}
                    />
                    {errors.companyName && (
                      <p className="text-red-500 text-sm">{errors.companyName}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="companyNif">NIF (optionnel)</Label>
                    <Input
                      id="companyNif"
                      name="companyNif"
                      value={formData.companyNif}
                      onChange={handleChange}
                      placeholder="Numéro d'identification fiscale"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="companyAddress">Adresse (optionnel)</Label>
                  <Textarea
                    id="companyAddress"
                    name="companyAddress"
                    value={formData.companyAddress}
                    onChange={handleChange}
                    placeholder="Adresse complète de l'entreprise"
                    rows={2}
                  />
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="companyEmail">Email de facturation *</Label>
                    <Input
                      id="companyEmail"
                      name="companyEmail"
                      type="email"
                      value={formData.companyEmail}
                      onChange={handleChange}
                      placeholder="facturation@entreprise.com"
                      className={errors.companyEmail ? 'border-red-500' : ''}
                    />
                    {errors.companyEmail && (
                      <p className="text-red-500 text-sm">{errors.companyEmail}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="companyPhone">Téléphone *</Label>
                    <Input
                      id="companyPhone"
                      name="companyPhone"
                      value={formData.companyPhone}
                      onChange={handleChange}
                      placeholder="+223 XX XX XX XX"
                      className={errors.companyPhone ? 'border-red-500' : ''}
                    />
                    {errors.companyPhone && (
                      <p className="text-red-500 text-sm">{errors.companyPhone}</p>
                    )}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contactName">Nom du contact *</Label>
                  <Input
                    id="contactName"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    placeholder="Prénom Nom"
                    className={errors.contactName ? 'border-red-500' : ''}
                  />
                  {errors.contactName && (
                    <p className="text-red-500 text-sm">{errors.contactName}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Payment security info */}
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Lock className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-green-800">Paiement 100% sécurisé</h4>
                    <p className="text-sm text-green-600">
                      Vos données sont protégées par PayTech. Nous n'avons jamais accès à vos informations bancaires.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Order summary */}
          <div className="space-y-4">
            <Card className="sticky top-4">
              <CardHeader className="bg-gradient-to-r from-webtech-blue to-blue-700 text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <Crown className="w-5 h-5" />
                  Plan BUSINESS
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                {/* Plan details */}
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Utilisateurs</span>
                    <span className="font-medium">{userCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Facturation</span>
                    <span className="font-medium">
                      {frequency === 'yearly' ? 'Annuelle' : 'Mensuelle'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Prix unitaire</span>
                    <span className="font-medium">
                      {formatPrice(priceInfo.pricePerUser)} FCFA/mois
                    </span>
                  </div>
                </div>
                
                <Separator />
                
                {/* Modules included */}
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Modules inclus</h4>
                  <ul className="space-y-1 text-sm">
                    {[
                      'HR People Mapping™',
                      'HR Performance System™',
                      'HR Skills Intelligence™',
                      'Analyse IA',
                      'Reporting avancé'
                    ].map((module, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-slate-600">
                        <Check className="w-4 h-4 text-green-500" />
                        {module}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <Separator />
                
                {/* Price breakdown */}
                <div className="space-y-2">
                  <div className="flex justify-between text-slate-600">
                    <span>Sous-total</span>
                    <span>{formatPrice(priceInfo.subtotal)} FCFA</span>
                  </div>
                  {priceInfo.discountAmount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Réduction {promoCode && `(${promoCode})`}</span>
                      <span>-{formatPrice(priceInfo.discountAmount)} FCFA</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold pt-2 border-t">
                    <span>Total</span>
                    <span className="text-webtech-blue">
                      {formatPrice(priceInfo.totalAmount)} FCFA
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">
                    {frequency === 'yearly' ? 'Facturé une fois par an' : 'Facturé chaque mois'}
                  </p>
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-3">
                <Button
                  className="w-full bg-webtech-blue hover:bg-webtech-blue-hover"
                  size="lg"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Traitement...</>
                  ) : (
                    <><CreditCard className="w-4 h-4 mr-2" />Payer {formatPrice(priceInfo.totalAmount)} FCFA</>
                  )}
                </Button>
                <p className="text-xs text-center text-slate-500">
                  En cliquant sur "Payer", vous acceptez nos conditions générales de vente
                </p>
              </CardFooter>
            </Card>

            {/* Payment methods */}
            <Card>
              <CardContent className="p-4">
                <h4 className="text-sm font-medium text-slate-700 mb-3">Moyens de paiement acceptés</h4>
                <div className="flex items-center gap-3 flex-wrap">
                  <Badge variant="outline" className="py-1">
                    <Phone className="w-3 h-3 mr-1" /> Orange Money
                  </Badge>
                  <Badge variant="outline" className="py-1">
                    <Phone className="w-3 h-3 mr-1" /> Wave
                  </Badge>
                  <Badge variant="outline" className="py-1">
                    <CreditCard className="w-3 h-3 mr-1" /> Carte bancaire
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
